/*
 * Where a walkthrough stands, on the card that started it.
 *
 * Starting a walkthrough moves the operator to the console, and whatever
 * brought them back — a question, a second thought, the back button — they
 * come back to the card they started it from. It must say what happened
 * since: paused at which step, with the ways on from there. A card that only
 * ever offered to start again, or vanished, left them with no way to tell
 * a walkthrough under way from one never begun.
 */

import { FiArrowRight, FiPause, FiRotateCcw, FiX } from "react-icons/fi";
import { endTour, restartTour, resumeTour } from "./tourStore";
import { VIEW_CONSOLE, navigateTo } from "../appView";

const open = (t, nodeId) => {
  const step = t && t.steps[t.at];
  navigateTo(VIEW_CONSOLE, { node: nodeId, panel: step && step.panel, from: "console" });
};

/** resumeAndOpen picks the walkthrough up on the step it was left at. */
export const resumeAndOpen = (nodeId) => {
  const t = resumeTour();
  if (t) open(t, nodeId);
};

/** restartAndOpen runs the walkthrough again from its first step. */
export const restartAndOpen = (nodeId) => {
  const t = restartTour();
  if (t) open(t, nodeId);
};

/** pausedLine names where a paused walkthrough stands, in one sentence. */
export const pausedLine = (tour, noun = "step") => {
  const step = tour.steps[tour.at];
  return `Paused at ${noun} ${tour.at + 1} of ${tour.steps.length}${step ? `: ${step.title}` : ""}`;
};

/**
 * TourProgress is the paused state with its three ways on. `noun` is what the
 * card calls a step ("step" for a change, "stop" for a tour) and `what` what
 * it calls the whole ("walkthrough", "tour").
 */
export default function TourProgress({ tour, nodeId, noun = "step", what = "walkthrough" }) {
  if (!tour) return null;
  return (
    <div className="twk" role="status">
      <Styles />
      <div className="twk-line">
        <FiPause size={13} className="twk-mark" aria-hidden="true" />
        <span>{pausedLine(tour, noun)}</span>
      </div>
      <div className="twk-acts">
        <button type="button" className="twk-go" onClick={() => resumeAndOpen(nodeId)}>
          Pick up where you left off <FiArrowRight size={13} />
        </button>
        <button type="button" className="twk-btn" onClick={() => restartAndOpen(nodeId)}>
          <FiRotateCcw size={12} /> Start over
        </button>
        <button type="button" className="twk-btn" onClick={endTour}>
          <FiX size={12} /> Stop the {what}
        </button>
      </div>
    </div>
  );
}

const Styles = () => (
  <style>{`
.twk{
  margin-top:13px;border:1px solid rgba(103,232,249,.34);border-radius:12px;padding:11px 13px;
  background:linear-gradient(180deg,rgba(103,232,249,.09),rgba(129,140,248,.05));
}
.twk-line{display:flex;align-items:center;gap:8px;font-size:13.5px;font-weight:600;color:#E2E8F0}
.twk-mark{flex:none;color:#67E8F9}
.twk-acts{display:flex;flex-wrap:wrap;gap:8px;margin-top:10px}
.twk-go,.twk-btn{font:inherit;line-height:1;cursor:pointer;border-radius:9px;display:inline-flex;align-items:center}
.twk-go{
  gap:7px;border:0;padding:9px 15px;font-size:13px;font-weight:700;color:#0B0C11;
  background:linear-gradient(135deg,#67E8F9,#818CF8);
}
.twk-go:hover{filter:brightness(1.06)}
.twk-btn{
  gap:6px;border:1px solid rgba(148,163,184,.34);background:transparent;
  padding:8px 13px;font-size:12.5px;font-weight:600;color:#CBD5E1;
}
.twk-btn:hover{border-color:rgba(103,232,249,.6);color:#67E8F9}
`}</style>
);
